import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { formatFileSize } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Star,
  Paperclip,
  Download,
  Mail,
  Calendar,
  User,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ListTodo,
  CalendarPlus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface EmailAttachment {
  id: number;
  providerAttachmentId: string;
  filename: string;
  mimeType: string | null;
  size: number | null;
}

interface EmailAccount {
  id: number;
  provider: string;
  email: string;
}

interface SyncedEmailDetail {
  id: number;
  emailAccountId: number;
  providerMessageId: string;
  threadId: string | null;
  subject: string | null;
  snippet: string | null;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses: string | null;
  ccAddresses: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  isRead: boolean | null;
  isStarred: boolean | null;
  labels: string | null;
  hasAttachments: boolean | null;
  receivedAt: string;
  attachments: EmailAttachment[];
  account: EmailAccount;
}

interface EmailAnalysis {
  id: number;
  emailCategory: string | null;
  urgency: string | null;
  requiresAction: boolean | null;
  summary: string | null;
  keyPoints: string[] | null;
  extractedPeople: { name: string; role?: string; company?: string }[] | null;
  extractedCompanies: string[] | null;
  sentiment: string | null;
}

interface EmailSuggestion {
  id: number;
  suggestionType: "task" | "calendar_event";
  title: string;
  description: string | null;
  priority: string | null;
  category: string | null;
  suggestedDate: string | null;
  suggestedDuration: number | null;
  confidence: string;
  reasoning: string | null;
  status: "pending" | "accepted" | "rejected" | "dismissed";
}

interface AnalysisResponse {
  analysis: EmailAnalysis | null;
  suggestions: EmailSuggestion[];
}

function parseAddresses(json: string | null): { email: string; name?: string }[] {
  if (!json) return [];
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
}

function AddressDisplay({ addresses }: { addresses: { email: string; name?: string }[] }) {
  return (
    <span>
      {addresses.map((addr, i) => (
        <span key={i}>
          {i > 0 && ", "}
          {addr.name ? (
            <>
              <span className="font-medium">{addr.name}</span>
              <span className="text-muted-foreground"> &lt;{addr.email}&gt;</span>
            </>
          ) : (
            <span>{addr.email}</span>
          )}
        </span>
      ))}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colors = {
    high: "bg-green-100 text-green-800 border-green-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    low: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return (
    <Badge variant="outline" className={colors[confidence as keyof typeof colors] || colors.low}>
      {confidence} confidence
    </Badge>
  );
}

function UrgencyBadge({ urgency }: { urgency: string | null }) {
  if (!urgency) return null;
  const colors = {
    urgent: "bg-red-100 text-red-800 border-red-200",
    high: "bg-orange-100 text-orange-800 border-orange-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    low: "bg-blue-100 text-blue-800 border-blue-200",
  };
  return (
    <Badge variant="outline" className={colors[urgency as keyof typeof colors] || colors.low}>
      {urgency} urgency
    </Badge>
  );
}

function CategoryBadge({ category }: { category: string | null }) {
  if (!category) return null;
  const labels: Record<string, string> = {
    new_hire: "New Hire",
    termination: "Termination",
    enrollment: "Enrollment",
    renewal: "Renewal",
    open_enrollment: "Open Enrollment",
    compliance: "Compliance",
    billing: "Billing",
    meeting_request: "Meeting Request",
    claims: "Claims",
    plan_changes: "Plan Changes",
    general: "General",
  };
  return (
    <Badge variant="secondary">{labels[category] || category}</Badge>
  );
}

export default function EmailDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const emailId = params.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAnalysis, setShowAnalysis] = useState(true);

  // Fetch email details
  const { data: email, isLoading, error } = useQuery<SyncedEmailDetail>({
    queryKey: ["/api/synced-emails", emailId],
    queryFn: async () => {
      const response = await fetch(`/api/synced-emails/${emailId}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Email not found");
        }
        throw new Error("Failed to fetch email");
      }
      return response.json();
    },
    enabled: !!emailId,
  });

  // Mark email as read when loaded
  useEffect(() => {
    if (email && !email.isRead) {
      fetch(`/api/synced-emails/${emailId}/read`, {
        method: "PATCH",
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/synced-emails"] });
      }).catch((err) => {
        console.error("Failed to mark email as read:", err);
      });
    }
  }, [email, emailId, queryClient]);

  // Fetch existing analysis
  const { data: analysisData, isLoading: analysisLoading } = useQuery<AnalysisResponse>({
    queryKey: ["/api/synced-emails", emailId, "analysis"],
    queryFn: async () => {
      const response = await fetch(`/api/synced-emails/${emailId}/analysis`);
      if (!response.ok) throw new Error("Failed to fetch analysis");
      return response.json();
    },
    enabled: !!emailId,
  });

  // Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/synced-emails/${emailId}/analyze`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Analysis failed");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails", emailId, "analysis"] });
      toast({ title: "Analysis complete", description: "AI has analyzed this email" });
    },
    onError: (error: Error) => {
      toast({ title: "Analysis failed", description: error.message, variant: "destructive" });
    },
  });

  // Re-analyze mutation
  const reanalyzeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/synced-emails/${emailId}/reanalyze`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Re-analysis failed");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails", emailId, "analysis"] });
      toast({ title: "Re-analysis complete", description: "AI has re-analyzed this email" });
    },
    onError: (error: Error) => {
      toast({ title: "Re-analysis failed", description: error.message, variant: "destructive" });
    },
  });

  // Accept suggestion mutation
  const acceptMutation = useMutation({
    mutationFn: async (suggestionId: number) => {
      const response = await fetch(`/api/email-suggestions/${suggestionId}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to accept");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails", emailId, "analysis"] });
      if (data.ticketId) {
        toast({ title: "Ticket created", description: `Ticket #${data.ticketId} has been created` });
      } else if (data.appointmentId) {
        toast({ title: "Event created", description: "Calendar event has been created" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to accept", description: error.message, variant: "destructive" });
    },
  });

  // Reject suggestion mutation
  const rejectMutation = useMutation({
    mutationFn: async (suggestionId: number) => {
      const response = await fetch(`/api/email-suggestions/${suggestionId}/reject`, {
        method: "POST",
      });
      if (!response.ok) throw new Error("Failed to reject");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails", emailId, "analysis"] });
      toast({ title: "Suggestion rejected" });
    },
  });

  const handleDownloadAttachment = async (attachment: EmailAttachment) => {
    try {
      const response = await fetch(
        `/api/synced-emails/attachments/${attachment.id}/download`
      );
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="w-10 h-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !email) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setLocation("/emails")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Inbox
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Email Not Found</h3>
            <p className="text-muted-foreground">
              This email may have been deleted or you don't have access to it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const toAddresses = parseAddresses(email.toAddresses);
  const ccAddresses = parseAddresses(email.ccAddresses);
  const analysis = analysisData?.analysis;
  const suggestions = analysisData?.suggestions || [];
  const pendingSuggestions = suggestions.filter((s) => s.status === "pending");

  // Basic HTML sanitization
  const sanitizeHtml = (html: string): string => {
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, "");
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]+/gi, "");
    sanitized = sanitized.replace(/javascript:/gi, "");
    return sanitized;
  };

  const sanitizedHtml = email.bodyHtml ? sanitizeHtml(email.bodyHtml) : null;

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setLocation("/emails")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Inbox
          </Button>
          <div className="flex items-center gap-2">
            {email.isStarred && (
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            )}
            <Badge variant="outline">{email.account.provider}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email Content - Takes 2 columns on large screens */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="space-y-4">
                <h1 className="text-2xl font-bold">
                  {email.subject || "(no subject)"}
                </h1>

                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">From: </span>
                      <span className="font-medium">{email.fromName}</span>
                      {email.fromAddress && (
                        <span className="text-muted-foreground">
                          {" "}&lt;{email.fromAddress}&gt;
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">To: </span>
                      <AddressDisplay addresses={toAddresses} />
                    </div>
                  </div>

                  {ccAddresses.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div>
                        <span className="text-muted-foreground">Cc: </span>
                        <AddressDisplay addresses={ccAddresses} />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {format(new Date(email.receivedAt), "PPpp")}
                    </span>
                  </div>
                </div>
              </CardHeader>

              <Separator />

              {/* Attachments */}
              {email.attachments && email.attachments.length > 0 && (
                <>
                  <div className="px-6 py-4 bg-muted/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Paperclip className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {email.attachments.length} Attachment
                        {email.attachments.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {email.attachments.map((attachment) => (
                        <Button
                          key={attachment.id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAttachment(attachment)}
                          className="gap-2"
                        >
                          <Download className="w-4 h-4" />
                          <span className="truncate max-w-[200px]">
                            {attachment.filename}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(attachment.size, { emptyText: "Unknown size" })})
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Body */}
              <CardContent className="pt-6">
                {sanitizedHtml ? (
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  />
                ) : email.bodyText ? (
                  <pre className="whitespace-pre-wrap font-sans text-sm">
                    {email.bodyText}
                  </pre>
                ) : (
                  <p className="text-muted-foreground italic">No content available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI Analysis Panel - Takes 1 column on large screens */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <CardTitle className="text-lg">AI Analysis</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAnalysis(!showAnalysis)}
                  >
                    {showAnalysis ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>

              {showAnalysis && (
                <CardContent className="space-y-4">
                  {!analysis ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-4">
                        <Sparkles className="w-6 h-6 text-purple-500" />
                      </div>
                      <h4 className="text-sm font-medium mb-2">No Analysis Yet</h4>
                      <p className="text-sm text-muted-foreground mb-6 max-w-[200px] mx-auto">
                        Analyze this email to get AI-powered task and event suggestions
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={() => analyzeMutation.mutate()}
                            disabled={analyzeMutation.isPending}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Sparkles className={`w-4 h-4 mr-2 ${analyzeMutation.isPending ? "animate-pulse" : ""}`} />
                            {analyzeMutation.isPending ? "Analyzing..." : "Analyze with AI"}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Use AI to extract tasks and calendar events from this email</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ) : (
                    <>
                      {/* Analysis Summary */}
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <CategoryBadge category={analysis.emailCategory} />
                          <UrgencyBadge urgency={analysis.urgency} />
                          {analysis.requiresAction && (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Action Required
                            </Badge>
                          )}
                        </div>

                        {analysis.summary && (
                          <p className="text-sm text-muted-foreground">
                            {analysis.summary}
                          </p>
                        )}

                        {analysis.keyPoints && analysis.keyPoints.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Key Points:</p>
                            <ul className="text-sm space-y-1">
                              {(analysis.keyPoints as string[]).map((point, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-muted-foreground">•</span>
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="pt-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => reanalyzeMutation.mutate()}
                                disabled={reanalyzeMutation.isPending}
                              >
                                <RefreshCw className={`w-4 h-4 mr-2 ${reanalyzeMutation.isPending ? "animate-spin" : ""}`} />
                                Re-analyze
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Run AI analysis again to get fresh suggestions</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>

                      <Separator />

                      {/* Suggestions */}
                      <div>
                        <h4 className="text-sm font-medium mb-3">
                          Suggestions ({pendingSuggestions.length} pending)
                        </h4>

                        {suggestions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No suggestions generated for this email.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {suggestions.map((suggestion) => (
                              <div
                                key={suggestion.id}
                                className={`border rounded-lg p-3 space-y-2 ${
                                  suggestion.status === "accepted"
                                    ? "bg-green-50 border-green-200"
                                    : suggestion.status === "rejected"
                                    ? "bg-gray-50 border-gray-200 opacity-60"
                                    : ""
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    {suggestion.suggestionType === "task" ? (
                                      <ListTodo className="w-4 h-4 text-blue-500" />
                                    ) : (
                                      <CalendarPlus className="w-4 h-4 text-green-500" />
                                    )}
                                    <span className="text-sm font-medium">
                                      {suggestion.title}
                                    </span>
                                  </div>
                                  {suggestion.status === "accepted" && (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  )}
                                  {suggestion.status === "rejected" && (
                                    <XCircle className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>

                                {suggestion.description && (
                                  <p className="text-xs text-muted-foreground">
                                    {suggestion.description}
                                  </p>
                                )}

                                <div className="flex flex-wrap gap-1">
                                  <ConfidenceBadge confidence={suggestion.confidence} />
                                  {suggestion.priority && (
                                    <Badge variant="outline" className="text-xs">
                                      {suggestion.priority}
                                    </Badge>
                                  )}
                                  {suggestion.suggestedDuration && (
                                    <Badge variant="outline" className="text-xs">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {suggestion.suggestedDuration} min
                                    </Badge>
                                  )}
                                </div>

                                {suggestion.reasoning && (
                                  <p className="text-xs text-muted-foreground italic">
                                    {suggestion.reasoning}
                                  </p>
                                )}

                                {suggestion.status === "pending" && (
                                  <div className="flex gap-2 pt-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="default"
                                          onClick={() => acceptMutation.mutate(suggestion.id)}
                                          disabled={acceptMutation.isPending}
                                        >
                                          <CheckCircle2 className="w-3 h-3 mr-1" />
                                          Accept
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          {suggestion.suggestionType === "task"
                                            ? "Create a ticket from this suggestion"
                                            : "Create a calendar event from this suggestion"}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => rejectMutation.mutate(suggestion.id)}
                                          disabled={rejectMutation.isPending}
                                        >
                                          <XCircle className="w-3 h-3 mr-1" />
                                          Reject
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Dismiss this suggestion</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
